import { BottomCTAProps } from '@/components/homeowners/BottomCTA';
import type { ClosingProps } from '@/components/homeowners/Closing';
import type { CoverageProps } from '@/components/homeowners/coverage/Coverage';
import type { FaqProps } from '@/components/homeowners/faq/Faq';
import { faqInfo } from '@/components/homeowners/faq/FaqData';
import type { HeroProps } from '@/components/homeowners/Hero';
import type { SwitchingProps } from '@/components/homeowners/Switching';
import type { TrustedProps } from '@/components/homeowners/Trusted';
import type { CustomersReviewsProps, ProductsProps } from '@/components/shared';
import { PRODUCT_HOME_US_HOMEOWNERS } from '@/components/shared/BuildByYou/defaults';
import { sharedConfig } from '@/pageConfig/shared';
import type { HoBottomLogosProps } from '@/pageTemplate/HomeownersPageTemplate';
import damageImgLg from '@/static/assets/homeowners/coverage/lg/damaged_darkbg.svg';
import fallenTreeImgLg from '@/static/assets/homeowners/coverage/lg/fallenTree_darkbg.svg';
import injuryImgLg from '@/static/assets/homeowners/coverage/lg/injury_darkbg.svg';
import lawImgLg from '@/static/assets/homeowners/coverage/lg/law_darkbg.svg';
import theftImgLg from '@/static/assets/homeowners/coverage/lg/theft_darkbg.svg';
import unlivableImgLg from '@/static/assets/homeowners/coverage/lg/unlivable_darkbg.svg';
import damageImgSm from '@/static/assets/homeowners/coverage/sm/damaged_darkbg.svg';
import fallenTreeImgSm from '@/static/assets/homeowners/coverage/sm/fallenTree_darkbg.svg';
import injuryImgSm from '@/static/assets/homeowners/coverage/sm/injury_darkbg.svg';
import lawImgSm from '@/static/assets/homeowners/coverage/sm/law_darkbg.svg';
import theftImgSm from '@/static/assets/homeowners/coverage/sm/theft_darkbg.svg';
import unlivableImgSm from '@/static/assets/homeowners/coverage/sm/unlivable_darkbg.svg';
import ogImage from '@/static/assets/homeowners/og.png';
import windowImage from '@/static/assets/homeowners/window_US.svg';
import type { BasePageConfig } from '../../BasePageConfig';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade Homeowners Insurance',
  description: 'Insurance policies for homeowners',
  image: [
    'https://www.lemonade.com/assets/home/homeowners/right_homeowners_mobile-af50b03b1ecfb81df02efe6a2a19bf7e4e07b565751c22483da27f612caeadb1.svg',
    'https://www.lemonade.com/assets/home/homeowners/right_homeowners-af94926a22ed9f66fbabc8d23a6f705b1c22ea0de0b82e0de3089f081bf8b13a.svg',
  ],
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '25',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does a Lemonade Homeowners policy cover?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Your policy covers your home and property for damage caused by things like wildfires, extreme weather, crime, and vandalism. It also protects you against legal claims for damage or injury you accidentally cause others. Learn more about what\'s covered <a href="https://www.lemonade.com/homeowners/explained/what-homeowners-insurance-covers/">here</a>.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does homeowners insurance cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Homeowners insurance at Lemonade <a href="https://www.lemonade.com/homeowners/explained/homeowners-insurance-cost/" target="_blank">starts at $25/month</a>, but varies by state and depends on factors including your house’s age, the cost to rebuild, and building materials, as well as the coverage and add-ons you choose. You can lower your homeowners insurance cost <a href="https://www.lemonade.com/homeowners/explained/how-to-buy-cheap-home-insurance-online/" target="_blank">by following these steps</a>.',
      },
    },
    {
      '@type': 'Question',
      name: 'What bundle options does Lemonade offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In addition to renters insurance, Lemonade offers homeowners, car, pet, and term life policies. You can unlock an automatic bundle discount with any combination, <a href="https://www.lemonade.com/renters/explained/renters-and-car-insurance-bundle/" target=“_blank”>like car and renters</a>, so it’s both a great way to consolidate your insurance needs and an opportunity to save.',
      },
    },
  ],
};

export interface Config extends BasePageConfig {
  hero: HeroProps;

  customersReviews: CustomersReviewsProps;
  bottomLogos: HoBottomLogosProps;
  products: ProductsProps;
  trusted: TrustedProps;
  closing: Omit<ClosingProps, 't'>;
  switching: SwitchingProps;
  coverage: CoverageProps;
  bundleSection?: unknown;
  faq: FaqProps;
  bottomCTA: BottomCTAProps;
}

export const config: Config = {
  layout: {
    product: PRODUCT_HOME_US_HOMEOWNERS,
    ...sharedConfig['en-US'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-US'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-US'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-US'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  seo: {
    title: 'Homeowners insurance that’s built for real life | Lemonade',
    metaTitle: 'Homeowners insurance that’s built for real life | Lemonade',
    description:
      'Get the best-loved homeowners insurance in America. 100% digital. No paperwork, zero hassle. Starting at $25/month.',
    jsonld: [productJsonld, faqSchema],
    ogImage: ogImage.src,
  },
  hero: {
    title: 'America’s most loved\nhomeowners insurance',
    subtitle: 'Zero hassle. Great prices.\nTrusted by your lender.',
  },

  customersReviews: {
    title: 'Ranked #1 in America, second year in a row!',
    subtitle:
      'Lemonade was voted the most loved insurance in America\nby Supermoney, Forbes, Clearsurance, and others.',
    darkBackground: true,
    showFullStars: true,
  },
  trusted: {},
  closing: {
    showBundleSection: false,
  },
  switching: {},
  coverage: {
    data: [
      {
        title: 'If your home\nis damaged',
        description: 'We’ll cover the cost to reconstruct your house.',
        img: {
          src: {
            lg: damageImgLg,
            sm: damageImgSm,
          },
          alt: 'home insurance will cover reconstruction costs',
        },
      },
      {
        title: 'If a tree falls on\nyour shed',
        description: 'We’ll help cover the cost of repairing structures on your property like sheds and garages.',
        img: {
          src: {
            lg: fallenTreeImgLg,
            sm: fallenTreeImgSm,
          },
          alt: 'tree damage coverage',
        },
      },
      {
        title: 'If your home\nis unlivable',
        description: 'We’ll cover expenses until you get back on your feet.',
        img: {
          src: {
            lg: unlivableImgLg,
            sm: unlivableImgSm,
          },
          alt: 'additional living expenses',
        },
      },
      {
        title: 'If your stuff\ngets stolen',
        description: 'We’ll help cover the costs to replace it - yes, even if it happens away from home.',
        img: {
          src: {
            lg: theftImgLg,
            sm: theftImgSm,
          },
          alt: 'replacement costs coverage',
        },
      },
      {
        title: 'If you’re sued\nfor liability',
        description: 'You’ll need a lawyer to defend you, so we’ll help cover those costs.',
        img: {
          src: {
            lg: lawImgLg,
            sm: lawImgSm,
          },
          alt: 'homeowners insurance covers lawyer costs',
        },
      },
      {
        title: 'If a visitor\ngets injured',
        description: 'You could get stuck covering the costs of their medical bills - we’ll help cover those costs.',
        img: {
          src: {
            lg: injuryImgLg,
            sm: injuryImgSm,
          },
          alt: 'medical bills are covered as well',
        },
      },
    ],
  },
  faq: {
    darkBackground: true,
    faqInfo,
  },
  bottomCTA: {
    windowImage,
  },
  bottomLogos: {
    layout: {
      mt: [0, -30],
    },
    title: 'Giving society a push\nfor the better',
    subtitle:
      'Lemonade is a public benefit corporation and a certified B-Corp. We take a flat fee up front,\nand donate unclaimed money to causes you choose.',
  },
  products: {
    title: 'More products from Lemonade',
    subtitle: 'Amazing savings when you bundle',
    trackingLabel: 'homeowners',
    products: [
      {
        title: 'Renters',
        subtitle: 'Coverage for your stuff, in\nand out of your home',
        price: 'From $5/mo',
        product: 'home_us_renters',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'renters insurance',
      },
      {
        title: 'Car',
        subtitle: 'Protect your car, passengers,\nand the planet',
        price: 'From $30/mo',
        product: 'car',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'car insurance',
      },
      {
        title: 'Pet',
        subtitle: 'Health insurance for furry\nfamily members',
        price: 'From $10/mo',
        product: 'pet',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'pet health insurance',
      },
      {
        title: 'Term Life',
        subtitle: 'Protecting the people\nyou love',
        price: 'From $8/mo',
        product: 'life',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'life insurance',
      },
    ],
  },
};
